import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { SelectedPanelEnum } from '@app/core';
import { InsurancesQueryParamsInterface } from '@app/my-zone/interfaces/insurances-query-params.interface';
import { PanelExpandableComponent } from '@app/shared/ux/panel-expandable/panel-expandable.component';
import {
    ErrorInterface,
    InsurancePolicyActionService,
    NewModal as ModalService,
    PolicyValidityStatusCodeEnum,
    PurchasedProductInterface,
} from 'outshared-lib';
import { InsuranceCancelAddedProductModal } from '../insurance-cancel-added-product-modal';
import { WucDisplayFieldModule, WucIconUiModule, WucLinkModule, WucRackModule } from '@inshared/website-ui-components';
import { NgIf } from '@angular/common';
import { TooltipModule } from '@app/shared/tooltip/tooltip.module';
import { DateFormatPipeModule } from '@inshared/shared/util-pipes';
import { PanelButtonModule } from '@app/shared/ux/panel-button/panel-button.module';

@Component({
    standalone: true,
    selector: 'ins-insurance-end-product-data',
    templateUrl: './insurance-end-product-data.component.html',
    styleUrls: ['./insurance-end-product-data.component.scss'],
    imports: [
        DateFormatPipeModule,
        NgIf,
        PanelButtonModule,
        TooltipModule,
        WucDisplayFieldModule,
        WucIconUiModule,
        WucLinkModule,
        WucRackModule,
    ]
})
export class InsuranceEndProductDataComponent {
    @Input() public purchasedProduct!: PurchasedProductInterface;
    @Input() public endProductForm!: PanelExpandableComponent;
    @Input() public endDateProductForm!: PanelExpandableComponent;

    public isDisabled: boolean;
    public errors: ErrorInterface[];

    constructor(
        private insurancePolicyActionService: InsurancePolicyActionService,
        private modalService: ModalService,
        private router: Router,
    ) {
        this.isDisabled = false;
        this.errors = [];
    }

    /**
     * No end date and product in future state.
     * When the customer has added a new product without start date.
     */
    public get canCancelAddedProduct(): boolean {
        return Boolean(
            !this.purchasedProduct?.product?.endDate &&
            (this.purchasedProduct?.product?.validityStatusCode === PolicyValidityStatusCodeEnum.Future),
        );
    }

    /**
     * With end date and product active ended state.
     * When the custome has set an end date for the product
     * but want to revive the product. ctrl + z ;).
     */
    public get canReviveProduct(): boolean {
        return Boolean(
            this.purchasedProduct?.product?.endDate &&
            (this.purchasedProduct?.product?.validityStatusCode === PolicyValidityStatusCodeEnum.ActiveEnded),
        );
    }

    /**
     * No end date and product active. When the customer wants
     * to set and end date to an active product.
     */
    public get canEndProduct(): boolean {
        return Boolean(
            !this.purchasedProduct?.product?.endDate &&
            (this.purchasedProduct?.product?.validityStatusCode === PolicyValidityStatusCodeEnum.Active),
        );
    }

    /**
     * Disbarment of customer and product is not ended. Customer
     * is able to add the end date of the product.
     */
    public get canAddEndDateProduct(): boolean {
        return Boolean(
            this.purchasedProduct?.product?.validityStatusCode === PolicyValidityStatusCodeEnum.Disbarred,
        );
    }

    /**
     * When a new product is added without start date
     * the customer is able to cancel the added product.
     */
    public cancelAddedProduct(): void {
        const modalRef = this.modalService
            .open<InsuranceCancelAddedProductModal>(InsuranceCancelAddedProductModal);

        if (modalRef) {
            modalRef.componentInstance.purchasedProduct = this.purchasedProduct;
        }
    }

    /**
     * When a product has an end date the customer
     * is able to revive the product.
     */
    public reviveProduct(): void {
        this.insurancePolicyActionService
            .reviveProduct$({
                productId: this.purchasedProduct.productId,
                productSequenceNumber: this.purchasedProduct.productSequenceNumber,
            })
            .subscribe({
                next: () => this.onReviveProductSuccess(),
                error: (errors) => this.onReviveProductError(errors),
            });

        this.isDisabled = true;
        this.errors = [];
    }

    private onReviveProductSuccess(): void {
        this.isDisabled = false;
        this.errors = [];
        this.closePanel();
    }

    private onReviveProductError(errors: ErrorInterface[]): void {
        this.isDisabled = false;
        this.errors = errors;
    }

    private closePanel(): void {
        const queryParams: InsurancesQueryParamsInterface = {
            panel: SelectedPanelEnum.Close,
            productId: this.purchasedProduct.productId,
            productSequenceNumber: this.purchasedProduct.productSequenceNumber,
            scroll: true,
        };

        this.router.navigate([], {
            queryParams,
            queryParamsHandling: 'merge',
        });
    }
}
